import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Toast } from '@shopify/polaris';

export interface ToastOptions {
  /** Render as an error (red) toast. */
  error?: boolean;
  /** Auto-dismiss duration in ms (Polaris default when omitted). */
  duration?: number;
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastState {
  message: string;
  error: boolean;
  duration?: number;
}

/**
 * Provides a `showToast` helper backed by a Polaris <Toast>. Must be rendered
 * inside a Polaris <Frame> (see AppFrame), which hosts the toast overlay.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    setToast({
      message,
      error: options?.error ?? false,
      duration: options?.duration,
    });
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <Toast
          content={toast.message}
          error={toast.error}
          duration={toast.duration}
          onDismiss={() => setToast(null)}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
